import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoadingTransition from "../components/LoadingTransition";

interface LoadingTransitionContextType {
  showTransition: (targetRoute: string, initialText?: string, completeText?: string) => void;
}

const LoadingTransitionContext = createContext<LoadingTransitionContextType | undefined>(undefined);

interface LoadingTransitionProviderProps {
  children: ReactNode;
}

export function LoadingTransitionProvider({ children }: LoadingTransitionProviderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [targetRoute, setTargetRoute] = useState<string>("");
  const [initialText, setInitialText] = useState<string>();
  const [completeText, setCompleteText] = useState<string>();
  const navigate = useNavigate();

  const showTransition = useCallback(
    (route: string, initial?: string, complete?: string) => {
      setTargetRoute(route);
      setInitialText(initial);
      setCompleteText(complete);
      setIsVisible(true);
    },
    []
  );

  const handleComplete = useCallback(() => {
    setIsVisible(false);
    if (targetRoute) {
      navigate(targetRoute);
    }
    setTargetRoute("");
    setInitialText(undefined);
    setCompleteText(undefined);
  }, [targetRoute, navigate]);

  return (
    <LoadingTransitionContext.Provider value={{ showTransition }}>
      {children}
      {isVisible && (
        <LoadingTransition
          isVisible={isVisible}
          onComplete={handleComplete}
          initialText={initialText}
          completeText={completeText}
        />
      )}
    </LoadingTransitionContext.Provider>
  );
}

export function useLoadingTransition() {
  const context = useContext(LoadingTransitionContext);
  if (!context) {
    throw new Error("useLoadingTransition must be used within a LoadingTransitionProvider");
  }
  return context;
}
